import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Avatar, Chip, Button, Container } from '@mui/material';
import { TrendingUp, Security, Speed, Analytics, Person, Email, Download, Telegram } from '@mui/icons-material';

// Hero Section
export const HeroSection = () => (
  <Box
    id="home"
    sx={{
      py: { xs: 8, md: 12 },
      textAlign: 'center',
      backgroundImage: 'url(/hero.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      borderRadius: 2,
      mb: 4,
    }}
  >
    <Typography
      variant="h1"
      component="h1"
      gutterBottom
      sx={{
        color: '#3E3E3E',
        textTransform: 'uppercase',
        textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
      }}
    >
      Brass Capital Partners
    </Typography>
    <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
      Portfolio of Trading Strategies
    </Typography>
    <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mb: 4, color: 'text.primary' }}>
      Combining the best trading teams for consistent returns
    </Typography>
    <Button
      variant="contained"
      size="large"
      onClick={() => document.getElementById('strategies').scrollIntoView({ behavior: 'smooth' })}
      sx={{ px: 4, py: 1.5 }}
    >
      Explore Our Strategies
    </Button>
  </Box>
);

// About Section
export const AboutSection = () => (
  <Box id="about" sx={{ py: { xs: 6, md: 8 } }}>
    <Typography variant="h2" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 6 }}>
      About Us
    </Typography>
    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={6}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
          Expertise in Digital Assets
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
          Brass Capital Partners specializes in composing all weather portfolios of sophisticated cryptocurrency trading strategies, 
          combining 5+ years of proprietary trading expertise with cutting-edge technology to navigate 
          the volatile digital asset markets.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
          Our team of experienced traders and quantitative analysts develops and implements 
          systematic strategies to capture alpha across various market conditions, from 
          trending markets to high-frequency arbitrage opportunities.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          {['Bitcoin', 'Ethereum', 'Arbitrage', 'Quantitative', 'Spot', 'Perps', 'Regulated', 'Options', 'All Weather', 'Portfolio', 'Fund', 'Risk Management'].map((skill) => (
            <Chip
              key={skill}
              label={skill}
              variant="outlined"
              sx={{ 
                borderColor: 'primary.main', 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'primary.light', color: 'white' }
              }}
            />
          ))}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Download our company overview teaser for a concise summary of strategies,
            risk management, and portfolio approach, along with a portfolio history.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            href="/BrassCP-Deck.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            startIcon={<Download />}
          >
            Download
          </Button>
        </Box>
      </Grid>
    </Grid>
  </Box>
);

// Strategies Section
export const StrategiesSection = () => (
  <Box id="strategies" sx={{ py: { xs: 6, md: 8 } }}>
    <Typography variant="h2" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 6 }}>
      Trading Strategies
    </Typography>
    <Grid container spacing={4}>
      {[
        // New strategies
        {
          title: 'Long/Short',
          description: 'Market-neutral and directional books constructed from relative value signals.',
          icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        {
          title: 'Funding Rate Arbitrage',
          description: 'Capture perp funding differentials with balanced spot/derivatives exposures.',
          icon: <Speed sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        {
          title: 'AI / Momentum',
          description: 'Machine-learning enhanced momentum and regime detection across digital assets.',
          icon: <Analytics sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        {
          title: 'Vol Surface Trading',
          description: 'Exploit implied/realized vol and skew via options structures and delta hedging.',
          icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        // Original strategies
        {
          title: 'Mean Reversion',
          description: 'Systematic reversion-to-mean signals across pairs, baskets, and factor spreads.',
          icon: <Analytics sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        {
          title: 'Arbitrage Trading',
          description: 'Cross-exchange and cross-asset arbitrage strategies to capture price discrepancies.',
          icon: <Speed sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        {
          title: 'Trend Following',
          description: 'Systematic approaches to identify and capitalize on sustained price movements.',
          icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
        {
          title: 'Risk Management',
          description: 'Comprehensive risk controls and portfolio optimization to preserve capital.',
          icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
        },
      ].map((strategy, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                {strategy.icon}
              </Box>
              <Typography variant="h6" gutterBottom>
                {strategy.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {strategy.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Team Section
export const TeamSection = () => (
  <Box id="team" sx={{ py: { xs: 6, md: 8 } }}>
    <Typography variant="h2" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 6 }}>
      Leadership Team
    </Typography>
    <Grid container spacing={4} justifyContent="center">
      {[
        {
          name: 'Milos Stojmenovic',
          title: 'Chief Executive Officer',
          description: '5+ years in prop market making and cross exchange arbitrage trading. Founder of Arbisoft Incorporated.',
          image: '/team/milos.jpg',
        },
        {
          name: 'Predrag Lugonja',
          title: 'Chief Investment Officer',
          description: '5+ years in prop market making and cross exchange arbitrage trading. Founder of Arbisoft Incorporated.',
          image: '/team/predrag.jpg',
        },
      ].map((member, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card sx={{ textAlign: 'center', p: 3 }}>
            <CardContent>
              <Avatar
                src={member.image}
                alt={member.name}
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                }}
              />
              <Typography variant="h6" gutterBottom>
                {member.name}
              </Typography>
              <Typography variant="subtitle1" color="primary.main" gutterBottom>
                {member.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {member.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Contact Section
export const ContactSection = () => (
  <Box id="contact" sx={{ py: { xs: 6, md: 8 } }}>
    <Typography variant="h2" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 6 }}>
      Contact Us
    </Typography>
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
          Get in Touch
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
          Interested in learning more about our investment strategies or becoming a client? 
          We'd love to hear from you.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Email sx={{ mr: 2, color: 'primary.main' }} />
          <Typography>ir@brasscp.com</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Telegram sx={{ mr: 2, color: 'primary.main' }} />
          <Typography>
            <a href="https://t.me/stojmen" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              @stojmen
            </a>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Telegram sx={{ mr: 2, color: 'primary.main' }} />
          <Typography>
            <a href="https://t.me/luganjap" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              @luganjap
            </a>
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Send us a message
          </Typography>
          <Box component="form" onSubmit={(e) => {
            e.preventDefault();
            const name = document.getElementById('contact-name')?.value || '';
            const email = document.getElementById('contact-email')?.value || '';
            const message = document.getElementById('contact-message')?.value || '';
            const subject = encodeURIComponent(`Website inquiry from ${name}`);
            const body = encodeURIComponent(`From: ${name} <${email}>\n\n${message}`);
            window.location.href = `mailto:ir@brasscp.com?subject=${subject}&body=${body}`;
          }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              id="contact-name"
              type="text"
              placeholder="Your Name"
              style={{
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px',
              }}
            />
            <input
              id="contact-email"
              type="email"
              placeholder="Your Email"
              style={{
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px',
              }}
            />
            <textarea
              id="contact-message"
              placeholder="Your Message"
              rows={4}
              style={{
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px',
                resize: 'vertical',
              }}
            />
            <Button type="submit" variant="contained" sx={{ alignSelf: 'flex-start' }}>
              Send Message
            </Button>
          </Box>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

// Footer
export const FooterSection = () => (
  <Box
    component="footer"
    sx={{
      py: 3,
      px: 2,
      mt: 'auto',
      backgroundColor: 'primary.main',
      color: 'white',
    }}
  >
    <Container maxWidth="lg">
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item xs={12} md={'auto'}>
          <Typography variant="body2" align="center">
            © 2025 Brass Capital Partners. All rights reserved.
          </Typography>
        </Grid>
        <Grid item xs={12} md={'auto'}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <Button color="inherit" href="/terms.html">Terms of Use</Button>
            <Button color="inherit" href="/privacy.html">Privacy Policy</Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// Export all sections in order
export const sections = [
  { id: 'home', title: 'Home', component: HeroSection },
  { id: 'about', title: 'About', component: AboutSection },
  { id: 'strategies', title: 'Strategies', component: StrategiesSection },
  { id: 'team', title: 'Team', component: TeamSection },
  { id: 'contact', title: 'Contact', component: ContactSection },
];